import useAvailableVehicles from '@/hooks/use-available-vehicles';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce, noop } from 'lodash';
import { useMemo, useState } from 'react';
import { AutoComplete } from '../ui/combobox/autocomplete';
import ConfirmDialog from '../ui/dialog/confirm-dialog';

type Item = {
  value: string;
  vin: string;
  label: string;
};

export function AvailableVehicleSearch({
  dealId,
  shortId,
  currentVehicleListingId,
  onConfirm,
}: {
  dealId: string;
  shortId: string;
  currentVehicleListingId: string;
  onConfirm: () => void;
}) {
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = debounce(setSearchValue, 200);

  const { vehicles } = useAvailableVehicles(searchValue);
  const items: Item[] = useMemo(() => {
    return (
      vehicles?.map((vehicleListingItem) => ({
        value: vehicleListingItem.id,
        vin: vehicleListingItem.vehicle?.vin ?? '',
        label: `${vehicleListingItem.YMMT} - ${vehicleListingItem.vehicle?.vin?.slice(-6)}`,
      })) ?? []
    );
  }, [vehicles]);

  const queryClient = useQueryClient();

  const swapVehicleMutation = useMutation({
    onMutate: async () => {
      // intentionally blank because we don't have vehicles on the FE app
    },
    mutationFn: async ({
      vehicleListingId,
      vin,
    }: {
      vehicleListingId: string;
      vin: string;
    }) => {
      return await pdkAxios.post(`/deals/${dealId}/swap-vehicle-listing`, {
        previous_vehicle_listing_id: currentVehicleListingId,
        new_vehicle_listing_id: vehicleListingId,
        new_vin: vin,
      });
    },
    onSuccess: () => {
      if (!selectedItem) {
        return;
      }

      vehicleListingStore.postSwapReindexing({
        previousVehicleListingId: currentVehicleListingId,
        newVehicleListingId: selectedItem.value,
        dealId: dealId,
      });
      setSelectedItem(null);
      onConfirm();
      toast({
        title: 'Successfully swapped vehicles',
        description: `Swapped vehicle to ${selectedItem?.label} ${selectedItem?.value}.`,
      });
    },
    onError: (error, variables) => {
      console.error('Error updating status', error, variables);
      toast({
        title: 'Error swapping vehicle',
        description: 'Please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleSwapVehicle = (vehicleListingId: string, vin: string) => {
    swapVehicleMutation.mutate({
      vehicleListingId,
      vin,
    });
  };

  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  return (
    <div>
      <AutoComplete
        onSelectedValueChange={(value) => {
          const item = items.find((item) => item.value === value);

          if (!item) {
            return;
          }

          setSelectedItem(item);
        }}
        searchValue={searchValue}
        onSearchValueChange={debouncedSearchValue}
        items={items}
        isLoading={false}
        emptyMessage="No vehicles found."
        placeholder="Search by VIN, year, make, model..."
      />
      <ConfirmDialog
        onConfirm={() => {
          if (!selectedItem) {
            return;
          }

          handleSwapVehicle(selectedItem.value, selectedItem.vin);
        }}
        dialogIsOpen={!!selectedItem}
        onOpen={noop}
        onClose={() => setSelectedItem(null)}
        title={`Swap Vehicle`}
        description={`Are you sure you want to swap to a ${selectedItem?.label} (VIN: ${selectedItem?.vin})?`}
      ></ConfirmDialog>
    </div>
  );
}
